/** Reviewer choice: should this artefact be approved for use? */

import clsx from "clsx";
import type { ArtefactApprovalDecision } from "./types";
import { approvalDecisionLabel, approvalDecisionClasses } from "./artefactApproval";

interface ArtefactApprovalSelectorProps {
  value: ArtefactApprovalDecision | null;
  onChange: (value: ArtefactApprovalDecision) => void;
  /** e.g. "Your decision" or "Would you approve this document?" */
  label?: string;
  compact?: boolean;
}

const OPTIONS: ArtefactApprovalDecision[] = ["approve", "not_approve"];

export function ArtefactApprovalSelector({
  value,
  onChange,
  label = "Would you approve this document?",
  compact = false,
}: ArtefactApprovalSelectorProps) {
  return (
    <div className={clsx("space-y-2", compact ? "" : "rounded-xl bg-white/[0.02] px-4 py-3 ring-1 ring-white/5")}>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <div className="flex flex-wrap gap-2">
        {OPTIONS.map((option) => {
          const selected = value === option;
          const styles = approvalDecisionClasses(option);
          return (
            <button
              key={option}
              type="button"
              onClick={() => onChange(option)}
              className={clsx(
                "rounded-lg px-3 py-1.5 text-xs font-medium ring-1 transition-colors",
                selected
                  ? clsx(styles.bg, styles.ring, styles.text)
                  : "bg-white/[0.03] text-slate-300 ring-white/10 hover:bg-white/[0.06]",
              )}
              data-testid={`artefact-approval-${option}`}
            >
              {approvalDecisionLabel(option)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ArtefactApprovalBadge({
  decision,
  prefix,
}: {
  decision: ArtefactApprovalDecision | null | undefined;
  prefix: string;
}) {
  if (!decision) {
    return <span className="text-xs text-slate-500">{prefix}: —</span>;
  }
  const styles = approvalDecisionClasses(decision);
  return (
    <span className={clsx("inline-flex items-center gap-1.5 text-xs font-medium", styles.text)}>
      <span className="font-normal text-slate-500">{prefix}</span>
      {approvalDecisionLabel(decision)}
    </span>
  );
}
